import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDebouncedCallback } from 'use-debounce';

import { checkTextWithCache } from '../api';
import { useGrammarCheckerContext } from '../context/GrammarCheckerProvider';
import { GrammarCheckAlert, GrammarCheckStats } from '../types';
import { getTextDiff } from '../utils/textUtils';

const useGrammarCheck = (
  isEnabled: boolean,
  onNewAlerts?: (alerts: GrammarCheckAlert[]) => void,
) => {
  const { config } = useGrammarCheckerContext();
  const previousTextRef = useRef('');

  const [alerts, setAlerts] = useState<GrammarCheckAlert[]>([]);
  const [dismissedAlerts, setDismissedAlerts] = useState<GrammarCheckAlert[]>(
    [],
  );
  const [stats, setStats] = useState<GrammarCheckStats | null>(null);
  const fetchesInProgressCount = useRef<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const debounceMs = config.debounceMs ?? 300;

  useEffect(() => {
    if (!isEnabled) {
      setAlerts([]);
      setDismissedAlerts([]);
      setStats(null);
    }
  }, [isEnabled]);

  const fetchGrammarCheckResults = useDebouncedCallback(
    async (text: string) => {
      fetchesInProgressCount.current = fetchesInProgressCount.current + 1;
      setIsLoading(true);
      try {
        const results = await checkTextWithCache(text, config);
        setAlerts((prevAlerts) => {
          const newAlerts = results.alerts.filter(
            (alert) =>
              !prevAlerts.some(
                (prevAlert) =>
                  prevAlert.absOffset === alert.absOffset &&
                  prevAlert.length === alert.length,
              ),
          );
          if (newAlerts.length > 0) {
            onNewAlerts?.(newAlerts);
          }
          return results.alerts;
        });
        setStats(results.stats);
      } catch (e) {
        // do not reset state on request fail/abort
      } finally {
        fetchesInProgressCount.current = fetchesInProgressCount.current - 1;
        setIsLoading(fetchesInProgressCount.current !== 0);
      }
    },
    debounceMs,
  );

  const recalculateAlertsAfterTextChange = (
    currentAlerts: GrammarCheckAlert[],
    changedOffset: number,
    originalLength: number,
    newLength: number,
  ): GrammarCheckAlert[] => {
    const charDiff = newLength - originalLength;
    if (charDiff === 0) {
      return currentAlerts;
    }

    return currentAlerts.reduce((acc, alert) => {
      if (
        alert.absOffset === changedOffset &&
        alert.length === originalLength
      ) {
        return acc;
      }

      if (alert.absOffset > changedOffset) {
        return [
          ...acc,
          {
            ...alert,
            absOffset: alert.absOffset + charDiff,
          },
        ];
      }

      return [...acc, alert];
    }, [] as GrammarCheckAlert[]);
  };

  const alertApplied = useCallback((alert: GrammarCheckAlert) => {
    setAlerts((prevAlerts) => {
      return prevAlerts.filter(
        (a) => a.absOffset !== alert.absOffset || a.length !== alert.length,
      );
    });
  }, []);

  const rerenderAlerts = useCallback(() => {
    setAlerts((prevAlerts) => [...prevAlerts]);
  }, []);

  const textChanged = useCallback(
    (newText: string) => {
      rerenderAlerts();

      if (!isEnabled || !newText || newText.length === 0) {
        setAlerts([]);
        setStats(null);
        return;
      }

      const oldText = previousTextRef.current;
      previousTextRef.current = newText;

      const { changedOffset, originalLength, newLength } = getTextDiff(
        oldText,
        newText,
      );

      if (originalLength !== newLength) {
        setAlerts((prevAlerts) => {
          return recalculateAlertsAfterTextChange(
            prevAlerts,
            changedOffset,
            originalLength,
            newLength,
          );
        });
      }

      fetchGrammarCheckResults(newText);
    },
    [isEnabled, fetchGrammarCheckResults],
  );

  const alertDismissed = useCallback((alert: GrammarCheckAlert) => {
    setDismissedAlerts((prev) => [...prev, alert]);
  }, []);

  const clearAlerts = useCallback(() => {
    setAlerts([]);
    setDismissedAlerts([]);
    setStats(null);
  }, []);

  const filteredAlerts = useMemo(() => {
    return alerts.filter(
      (alert) =>
        !dismissedAlerts.some(
          (dismissedAlert) =>
            dismissedAlert.absOffset === alert.absOffset &&
            dismissedAlert.length === alert.length,
        ),
    );
  }, [alerts, dismissedAlerts]);

  return {
    alerts: filteredAlerts,
    stats,
    alertApplied,
    alertDismissed,
    textChanged,
    isLoadingGrammarCheck: isLoading,
    clearAlerts,
  };
};

export default useGrammarCheck;
